import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { compareSectorNames } from "@/lib/sectorSort";
import { sectorSourceFor } from "@/lib/sectorSourcePolicy";
import { useSupabaseRealtimeInvalidation } from "@/hooks/useSupabaseRealtimeInvalidation";
import PolicyCompliancePanel from "@/components/risk/PolicyCompliancePanel";
import {
  resolveFundCbam,
  type FeBreakdownRow,
  type FeMappingRow,
  type FeSplitOverride,
} from "@/components/fe/feMatching";

interface MasterSector { id: string; name: string; sector_group: string; display_order: number; }
interface FundWeight { fund_id: string; portfolio: number; weight_pct: number; effective_date: string; }
interface SectorMapping { fund_id: string; sector_id: string; weighting: number; }
interface CbamFund { id: string; isin: string | null; }

export type BlendedSide = "p" | "r" | "dist" | "mm";

interface Props {
  side: BlendedSide;
  portfolios: readonly number[];
  title: string;
  description?: string;
  /** Column header for each portfolio (e.g. "P1", "Low", "MM"). */
  portfolioLabel: (p: number) => string;
  /** Optional risk-policy panel range. */
  policyRange?: "P" | "R";
}

const WEIGHTS_TABLE: Record<BlendedSide, "ba_cbam_fund_weights" | "ba_r_cbam_fund_weights" | "ba_dist_cbam_fund_weights" | "ba_mm_cbam_fund_weights"> = {
  p: "ba_cbam_fund_weights",
  r: "ba_r_cbam_fund_weights",
  dist: "ba_dist_cbam_fund_weights",
  mm: "ba_mm_cbam_fund_weights",
};

export default function BlendedSectorWeightings({
  side,
  portfolios,
  title,
  description,
  portfolioLabel,
  policyRange,
}: Props) {
  const weightsTable = WEIGHTS_TABLE[side];

  useSupabaseRealtimeInvalidation(
    `${side}-sector-weightings-summary`,
    [
      "ba_master_sectors",
      weightsTable,
      "ba_cbam_fund_sector_mappings",
      "fe_asset_breakdown",
      "fe_sector_mapping",
      "fe_fund_split_overrides",
    ],
    [
      ["ba-master-sectors"],
      [`ba-${side}-fund-weights-blended`],
      ["ba-fund-sector-mappings"],
      ["fe-asset-breakdown-latest"],
      ["fe-sector-mapping"],
      ["fe-split-overrides"],
      ["ba-cbam-funds-isin"],
    ],
  );

  const { data: sectors } = useQuery({
    queryKey: ["ba-master-sectors"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ba_master_sectors")
        .select("id, name, sector_group, display_order")
        .order("display_order");
      if (error) throw error;
      return data as MasterSector[];
    },
  });

  const { data: weights } = useQuery({
    queryKey: [`ba-${side}-fund-weights-blended`],
    queryFn: async () => {
      const { data, error } = await supabase
        .from(weightsTable)
        .select("fund_id, portfolio, weight_pct, effective_date")
        .order("effective_date", { ascending: false });
      if (error) throw error;
      const seen = new Set<string>();
      const latest: FundWeight[] = [];
      for (const w of (data as FundWeight[])) {
        const key = `${w.fund_id}::${w.portfolio}`;
        if (seen.has(key)) continue;
        seen.add(key);
        latest.push(w);
      }
      return latest;
    },
  });

  const { data: mappings } = useQuery({
    queryKey: ["ba-fund-sector-mappings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ba_cbam_fund_sector_mappings")
        .select("fund_id, sector_id, weighting");
      if (error) throw error;
      return data as SectorMapping[];
    },
  });

  const { data: cbamFunds } = useQuery({
    queryKey: ["ba-cbam-funds-isin"],
    queryFn: async () => {
      const { data, error } = await supabase.from("ba_cbam_funds").select("id, isin");
      if (error) throw error;
      return data as CbamFund[];
    },
  });

  const { data: feRows } = useQuery({
    queryKey: ["fe-asset-breakdown-latest"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("fe_asset_breakdown")
        .select("id, isin, fund_name, as_of_date, breakdown")
        .order("as_of_date", { ascending: false });
      if (error) throw error;
      const latest = new Map<string, FeBreakdownRow>();
      for (const r of data ?? []) {
        const row = r as any;
        if (!latest.has(row.isin)) {
          latest.set(row.isin, { ...row, breakdown: row.breakdown ?? {} });
        }
      }
      return Array.from(latest.values());
    },
  });

  const { data: feMapping } = useQuery({
    queryKey: ["fe-sector-mapping"],
    queryFn: async () => {
      const { data, error } = await supabase.from("fe_sector_mapping").select("*").order("display_order");
      if (error) throw error;
      return data as FeMappingRow[];
    },
  });

  const { data: feOverrides } = useQuery({
    queryKey: ["fe-split-overrides"],
    queryFn: async () => {
      const { data, error } = await supabase.from("fe_fund_split_overrides").select("*");
      if (error) throw error;
      return data as FeSplitOverride[];
    },
  });

  const heldFundIds = new Set(
    (weights ?? []).filter((w) => Number(w.weight_pct) > 0).map((w) => w.fund_id),
  );
  const sectorIdByName = new Map((sectors ?? []).map((s) => [s.name, s.id]));

  // Per fund, decide manual vs FE. Manual mapping wins whenever the fund has one; FE's
  // breakdown is used only as a fallback for a fund with no manual mapping, and a fund with
  // neither contributes nothing (excluded, never zeroed). sectorSourcePolicy.ts holds the rule
  // and the reasoning; this used to be a 50/50 average written as a bare literal here and again
  // in blendedPortfolioMatrix.ts.
  const matrix = new Map<string, number>();
  const totals = new Map<number, number>();

  if (weights && mappings && cbamFunds) {
    const mappingsByFund = new Map<string, SectorMapping[]>();
    for (const m of mappings) {
      if (!mappingsByFund.has(m.fund_id)) mappingsByFund.set(m.fund_id, []);
      mappingsByFund.get(m.fund_id)!.push(m);
    }

    const mappingByCol = new Map((feMapping ?? []).map((m) => [m.fe_column_name, m]));
    const overridesByIsinCol = new Map<string, FeSplitOverride[]>();
    for (const o of feOverrides ?? []) {
      const k = `${o.isin}::${o.fe_column_name}`;
      if (!overridesByIsinCol.has(k)) overridesByIsinCol.set(k, []);
      overridesByIsinCol.get(k)!.push(o);
    }
    const isinByFundId = new Map(
      cbamFunds.map((f) => [f.id, (f.isin ?? "").toUpperCase().trim()]),
    );
    const feByIsin = new Map<string, FeBreakdownRow>();
    for (const r of feRows ?? []) if (r.isin) feByIsin.set(r.isin.toUpperCase().trim(), r);

    for (const w of weights) {
      if (!heldFundIds.has(w.fund_id) || Number(w.weight_pct) <= 0) continue;

      const fundMappings = mappingsByFund.get(w.fund_id) ?? [];
      const hasManual = fundMappings.length > 0;

      // Compute FE allocation for this fund (sector name -> pct of fund).
      let feAlloc: Record<string, number> | null = null;
      const isin = isinByFundId.get(w.fund_id);
      if (isin) {
        const fe = feByIsin.get(isin);
        if (fe) {
          const { allocation, total } = resolveFundCbam(fe, mappingByCol, overridesByIsinCol);
          const hasUnresolved = Object.keys(allocation).some(
            (k) => k.startsWith("[needs split] ") || k === "Unknown",
          );
          // FE is usable only if it resolves fully (~100) into known sectors.
          if (total > 0 && !hasUnresolved && Math.abs(total - 100) < 1) {
            feAlloc = allocation;
          }
        }
      }
      const hasFe = feAlloc !== null;

      // Manual first, FE as backup. Shared with src/lib/blendedPortfolioMatrix.ts so the two
      // implementations of this arithmetic cannot drift — see sectorSourcePolicy.ts.
      const { manualShare, feShare } = sectorSourceFor(hasManual, hasFe);
      if (manualShare === 0 && feShare === 0) continue;

      // Manual contribution.
      if (manualShare > 0) {
        for (const m of fundMappings) {
          const key = `${w.portfolio}-${m.sector_id}`;
          const contribution = Number(w.weight_pct) * Number(m.weighting) * manualShare;
          matrix.set(key, (matrix.get(key) || 0) + contribution);
          totals.set(w.portfolio, (totals.get(w.portfolio) || 0) + contribution);
        }
      }

      // FE contribution.
      if (feShare > 0 && feAlloc) {
        for (const [sectorName, pct] of Object.entries(feAlloc)) {
          const sectorId = sectorIdByName.get(sectorName);
          if (!sectorId) continue;
          const key = `${w.portfolio}-${sectorId}`;
          const contribution = Number(w.weight_pct) * (Number(pct) / 100) * feShare;
          matrix.set(key, (matrix.get(key) || 0) + contribution);
          totals.set(w.portfolio, (totals.get(w.portfolio) || 0) + contribution);
        }
      }
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">{title}</h1>
        <p className="text-sm text-muted-foreground">
          {description ??
            "Final BM sector mapping: per (portfolio, sector), if both FE-derived and manually mapped sums are > 0 they are blended 50/50; otherwise the only contributing source is taken at 100%."}
        </p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">CBAM Sector Allocation by Portfolio</CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="text-left p-3 font-medium text-muted-foreground min-w-[240px] sticky left-0 bg-muted/50 z-10">
                  CBAM Master Sector
                </th>
                {portfolios.map((p) => (
                  <th key={p} className="text-center p-3 font-medium text-muted-foreground min-w-[100px]">
                    {portfolioLabel(p)} %
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...(sectors ?? [])]
                .sort((a, b) => compareSectorNames(a.name, b.name))
                .filter((sector) => portfolios.some((p) => (matrix.get(`${p}-${sector.id}`) || 0) > 0))
                .map((sector, idx) => {
                  const rowTotal = portfolios.reduce(
                    (s, p) => s + (matrix.get(`${p}-${sector.id}`) || 0),
                    0,
                  );
                  return (
                  <tr key={sector.id} className={cn("border-b", idx % 2 === 0 ? "bg-background" : "bg-muted/20")}>
                    <td
                      className={cn(
                        "p-3 text-xs font-medium sticky left-0 z-10",
                        idx % 2 === 0 ? "bg-background" : "bg-muted/20",
                      )}
                    >
                      <span>{sector.name}</span>
                    </td>
                    {portfolios.map((p) => {
                      const val = matrix.get(`${p}-${sector.id}`) || 0;
                      return (
                        <td key={p} className="p-3 text-center text-xs font-mono">
                          {val > 0 ? val.toFixed(2) : <span className="text-muted-foreground/40">—</span>}
                        </td>
                      );
                    })}
                  </tr>
                  );
                })}
              <tr className="border-t-2 bg-muted/40 font-semibold">
                <td className="p-3 text-xs sticky left-0 bg-muted/40 z-10">Total</td>
                {portfolios.map((p) => (
                  <td key={p} className="p-3 text-center text-xs font-mono">
                    {(totals.get(p) || 0).toFixed(2)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </CardContent>
      </Card>

      {policyRange && <PolicyCompliancePanel range={policyRange} />}
    </div>
  );
}
